import { StandardEvents } from '@shopify/events';
import { phAddToCart } from '@theme/ph-cart-add';

/* PHASES grid: image stepping, swatches, quick add, toggle filter */

async function addToCart(card, variantId, source) {
  if (!variantId || card.classList.contains('is-adding')) return;
  card.classList.add('is-adding');
  const { ok } = await phAddToCart(card, variantId, 1);
  card.classList.remove('is-adding', 'is-open');
  if (ok && source) {
    source.classList.add('is-added');
    setTimeout(() => source.classList.remove('is-added'), 1200);
  }
}

function showImage(card, index) {
  const media = card.querySelector('.ph-card__media');
  const imgs = [...card.querySelectorAll('.ph-card__img[data-index]')];
  if (!media || imgs.length < 2) return;
  const i = (index + imgs.length) % imgs.length;
  media.style.setProperty('--cur', String(i));
  media.classList.add('has-active');
  card.dataset.current = String(i);
}

function initGrid(root) {
  if (root.dataset.phReady) return;
  root.dataset.phReady = 'true';

  root.addEventListener('click', (e) => {
    const t = /** @type {HTMLElement} */ (e.target);
    const card = t.closest('[data-ph-card]');
    if (!card) return;

    // Tapping the photo/title: tag this card's photo box so the browser morphs it into the product page photo
    if (t.closest('.ph-card__link, .ph-card__title')) {
      document.querySelectorAll('[data-view-transition-triggered]').forEach((el) => {
        el.removeAttribute('data-view-transition-triggered');
        el.removeAttribute('data-view-transition-type');
      });
      const media = card.querySelector('.ph-card__media');
      media?.setAttribute('data-view-transition-type', 'product-image-transition');
      media?.setAttribute('data-view-transition-triggered', 'true');
      return;
    }

    const prev = t.closest('[data-ph-prev]');
    const next = t.closest('[data-ph-next]');
    if (prev || next) {
      e.preventDefault();
      const cur = Number(card.dataset.current || 0);
      showImage(card, cur + (next ? 1 : -1));
      return;
    }

    const plus = t.closest('[data-ph-plus]');
    if (plus) {
      e.preventDefault();
      if (plus.dataset.variantId) addToCart(card, plus.dataset.variantId, plus);
      else card.classList.toggle('is-open');
      return;
    }

    const size = t.closest('[data-ph-size]');
    if (size && !size.disabled) {
      e.preventDefault();
      addToCart(card, size.dataset.variantId, size);
      return;
    }

    const swatch = t.closest('[data-ph-swatch]');
    if (swatch) {
      e.preventDefault();
      card.querySelectorAll('[data-ph-swatch]').forEach((s) => s.classList.toggle('is-active', s === swatch));
      const target = card.querySelector(`.ph-card__img[data-media-id="${swatch.dataset.mediaId}"]`);
      if (target) showImage(card, Number(target.dataset.index));
    }
  });

  // Drag / swipe through a card's images (mouse and touch); the strip follows the finger
  root.addEventListener('dragstart', (e) => {
    if (/** @type {HTMLElement} */ (e.target).closest?.('.ph-card__media')) e.preventDefault();
  });
  let drag = null;
  root.addEventListener('pointerdown', (e) => {
    const media = /** @type {HTMLElement} */ (e.target).closest?.('.ph-card__media');
    const card = media?.closest('[data-ph-card]');
    if (!media || !card || media.querySelectorAll('.ph-card__img[data-index]').length < 2) return;
    // Inside carousels a sideways drag scrolls the row of products, not the card's photos
    if (media.closest('.ph-recs__track, .ph-cart-recs__track')) return;
    if (/** @type {HTMLElement} */ (e.target).closest('button')) return;
    drag = { card, media, x: e.clientX, y: e.clientY, dx: 0, locked: null };
  });
  window.addEventListener('pointermove', (e) => {
    if (!drag) return;
    const dx = e.clientX - drag.x;
    const dy = e.clientY - drag.y;
    if (drag.locked === null && (Math.abs(dx) > 6 || Math.abs(dy) > 6)) drag.locked = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
    if (drag.locked !== 'x') return;
    drag.dx = dx;
    drag.media.classList.add('is-dragging');
    drag.media.style.setProperty('--drag', `${dx}px`);
  });
  const endDrag = () => {
    if (!drag) return;
    const { card, media, dx, locked } = drag;
    drag = null;
    media.classList.remove('is-dragging');
    media.style.setProperty('--drag', '0px');
    if (locked !== 'x' || Math.abs(dx) < 40) return;
    showImage(card, Number(card.dataset.current || 0) + (dx < 0 ? 1 : -1));
    // swallow the click that follows a drag so the card link doesn't open
    card.addEventListener('click', (ev) => { ev.preventDefault(); ev.stopPropagation(); }, { capture: true, once: true });
  };
  window.addEventListener('pointerup', endDrag);
  window.addEventListener('pointercancel', endDrag);


  // Toggle filter
  const toggle = root.querySelector('[data-ph-toggle]');
  const tag = root.dataset.filterTag;
  if (toggle && tag) {
    toggle.addEventListener('change', () => {
      const on = toggle.checked;
      root.classList.toggle('is-filtered', on);
      root.querySelectorAll('[data-ph-card]').forEach((card) => {
        const tags = (card.dataset.tags || '').split(',');
        card.classList.toggle('is-hidden', on && !tags.includes(tag));
      });
    });
  }
}

const boot = () => document.querySelectorAll('[data-ph-grid]').forEach(initGrid);
boot();
document.addEventListener('shopify:section:load', boot);

// Close any open size picker when tapping elsewhere
document.addEventListener('click', (e) => {
  document.querySelectorAll('.ph-card.is-open').forEach((card) => {
    if (!card.contains(/** @type {Node} */ (e.target))) card.classList.remove('is-open');
  });
});

// The cart drawer re-renders its contents (incl. "You may also like") after every cart change
document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  event.promise?.finally(() => setTimeout(boot, 60));
});
new MutationObserver(boot).observe(document.body, { childList: true, subtree: true });

// Cart "You may also like" row: drag to scroll with the mouse, and a drag/swipe never opens a product.
// Delegated at document level because the cart drawer re-renders this row after every cart change.
(() => {
  let state = null;
  document.addEventListener('pointerdown', (e) => {
    const track = /** @type {HTMLElement} */ (e.target).closest?.('.ph-cart-recs__track');
    if (!track || /** @type {HTMLElement} */ (e.target).closest('button')) return;
    state = { track, x: e.clientX, y: e.clientY, left: track.scrollLeft, moved: 0, mouse: e.pointerType === 'mouse' };
  });
  document.addEventListener('pointermove', (e) => {
    if (!state) return;
    state.moved = Math.max(state.moved, Math.abs(e.clientX - state.x), Math.abs(e.clientY - state.y));
    if (state.mouse) {
      if (state.moved > 5) state.track.classList.add('is-dragging');
      state.track.scrollLeft = state.left - (e.clientX - state.x);
    }
  });
  const end = () => {
    if (!state) return;
    const { track, moved } = state;
    state = null;
    if (moved > 8) {
      track.addEventListener('click', (ev) => { ev.preventDefault(); ev.stopPropagation(); }, { capture: true, once: true });
      setTimeout(() => track.classList.remove('is-dragging'), 0);
    } else {
      track.classList.remove('is-dragging');
    }
  };
  document.addEventListener('pointerup', end);
  document.addEventListener('pointercancel', () => { if (state) state.track.classList.remove('is-dragging'); state = null; });
  // A finger that scrolled the row (browser-handled) must not count as a tap either
  document.addEventListener('scroll', (e) => {
    if (state && e.target === state.track) state.moved = 99;
  }, { capture: true, passive: true });
  document.addEventListener('dragstart', (e) => {
    if (/** @type {HTMLElement} */ (e.target).closest?.('.ph-cart-recs__track')) e.preventDefault();
  });
})();
